import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MapGeocoder } from '@angular/google-maps';
import { Router } from '@angular/router';

import { AlertController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { circle } from '@turf/circle';

import { Feature } from 'geojson';
import {
  camelCase,
  compact,
  filter as lodashFilter,
  flatten,
  forEach,
  isEmpty,
  isEqual,
  isNull,
  map as lodashMap,
  uniq
} from 'lodash-es';
import { DateTime } from 'luxon';
import { map, of, switchMap, withLatestFrom } from 'rxjs';
import { catchError, concatMap, filter, mergeMap } from 'rxjs/operators';
import { GeoJSONGeometryOrNull, parse } from 'wellknown';

import { yearMonthDayDateFormat } from '../../constants/datetime.constant';
import { KeyRefreshEnum } from '../../enums/key-refresh';
import { WalkingFarmNewTabEnum, WalkingFarmPropertiesOwnerStatusFilterEnum } from '../../enums/walking-farm';
import { getMainRouteByPropertySearchType } from '../../helpers/property-profile';
import { AdminPortalApiService } from '../../misc/services/adminPortalApi.service';
import { FileService } from '../../misc/services/file.service';
import { PropertyForceApiService } from '../../misc/services/propertyForceApi.service';
import { ReportApiService } from '../../misc/services/reportApi.service';
import { RefreshKeyResponse } from '../../typings/property-force-api';
import {
  PolygonFarmCoordinates,
  WalkingFarm,
  WalkingFarmAlertsResponse,
  WalkingFarmCreateResponse,
  WalkingFarmLoadSavedFarmsResponse,
  WalkingFarmPropertyAlertsResponse
} from '../../typings/walking-farm';
import * as fromMapActions from '../actions/map.actions';
import * as fromWalkingFarmActions from '../actions/walking-farm.actions';
import { StoredWalkingFarm } from '../models/walking-farm.model';
import * as fromDataProviderSelectors from '../selectors/data-provider.selectors';
import * as fromDeviceSelectors from '../selectors/device.selectors';
import * as fromSettingSelectors from '../selectors/setting.selectors';
import * as fromUserSelectors from '../selectors/user.selectors';
import * as fromWalkingFarmSelectors from '../selectors/walking-farm.selectors';

@Injectable()
export class WalkingFarmEffects {
  private actions$ = inject(Actions);
  private adminPortalApiService = inject(AdminPortalApiService);
  private alertController = inject(AlertController);
  private fileService = inject(FileService);
  private mapGeocoder = inject(MapGeocoder);
  private propertyForceApiService = inject(PropertyForceApiService);
  private reportApiService = inject(ReportApiService);
  private router = inject(Router);
  private store = inject(Store);

  getPolygonPointsFromCircle(center: google.maps.LatLngLiteral | null, radius: number) {
    const circleInstance = circle([center?.lng || 0, center?.lat || 0], radius, { steps: 12, units: 'meters' });
    return {
      points: flatten(
        lodashMap(circleInstance.geometry.coordinates, (coordinate) =>
          lodashMap(coordinate, (position) => ({
            x: position[1],
            y: position[0]
          }))
        )
      )
    };
  }

  refreshKey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromWalkingFarmActions.refreshKey),
      withLatestFrom(
        this.store.pipe(select(fromDataProviderSelectors.selectDataProviderCredentials)),
        this.store.pipe(select(fromDeviceSelectors.selectPropertyForceDeviceData)),
        this.store.pipe(select(fromSettingSelectors.selectReferralCode))
      ),
      map((result) => ({
        deviceData: result[2],
        office: result[3],
        password: result[1].password ?? '',
        username: result[1].username ?? ''
      })),
      switchMap((query) =>
        this.propertyForceApiService.getData<RefreshKeyResponse>('/GetFarmKeyWithTACCheck', query).pipe(
          switchMap((response) => of(fromWalkingFarmActions.refreshKeySuccess({ payload: { key: response.d.Key } }))),
          catchError((error: HttpErrorResponse) =>
            of(
              fromWalkingFarmActions.refreshKeyFailure({
                payload: { error }
              })
            )
          )
        )
      )
    )
  );

  loadSavedFarmsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromWalkingFarmActions.loadSavedFarmsList, fromWalkingFarmActions.markAlertsAsReadSuccess),
      withLatestFrom(
        this.store.pipe(select(fromSettingSelectors.selectParseType)),
        this.store.pipe(select(fromSettingSelectors.selectApplicationKey)),
        this.store.pipe(select(fromUserSelectors.selectEmail))
      ),
      map((data) => ({
        appName: data[2],
        email: data[3],
        parseType: data[1]
      })),
      switchMap(({ appName, email, parseType }) =>
        this.adminPortalApiService
          .sendRequest('GET', `/v2/farms/history`, {
            params: this.adminPortalApiService.getQueryParams({
              appName: appName,
              email: email,
              parseType: parseType
            })
          })
          .pipe(
            map((payload: WalkingFarmLoadSavedFarmsResponse[]) =>
              fromWalkingFarmActions.loadSavedFarmsListSuccess({
                payload
              })
            ),
            catchError((error: HttpErrorResponse) =>
              of(
                fromWalkingFarmActions.loadSavedFarmsListFailure({
                  payload: { error }
                })
              )
            )
          )
      )
    )
  );

  loadAlerts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromWalkingFarmActions.loadAlerts),
      withLatestFrom(this.store.pipe(select(fromSettingSelectors.selectParseType))),
      switchMap(
        ([
          {
            payload: { id }
          },
          parseType
        ]) =>
          this.adminPortalApiService
            .sendRequest('GET', `/v2/farms/${id}/alerts`, {
              params: this.adminPortalApiService.getQueryParams({
                parseType: parseType
              })
            })
            .pipe(
              map((payload: WalkingFarmAlertsResponse) => fromWalkingFarmActions.loadAlertsSuccess({ payload })),
              catchError((error: HttpErrorResponse) =>
                of(
                  fromWalkingFarmActions.loadAlertsFailure({
                    payload: { error }
                  })
                )
              )
            )
      )
    )
  );

  loadPropertyAlerts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromWalkingFarmActions.loadPropertyAlerts),
      withLatestFrom(this.store.pipe(select(fromSettingSelectors.selectParseType))),
      switchMap(([{ payload }, parseType]) =>
        this.adminPortalApiService
          .sendRequest('GET', `/v2/farms/${payload.id}/property/${payload.propertyId}/alerts`, {
            params: this.adminPortalApiService.getQueryParams({
              parseType: parseType
            })
          })
          .pipe(
            map((payload: WalkingFarmPropertyAlertsResponse) =>
              fromWalkingFarmActions.loadPropertyAlertsSuccess({ payload })
            ),
            catchError((error: HttpErrorResponse) =>
              of(
                fromWalkingFarmActions.loadPropertyAlertsFailure({
                  payload: { error }
                })
              )
            )
          )
      )
    )
  );

  markAlertsAsRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromWalkingFarmActions.markAlertsAsRead),
      withLatestFrom(this.store.pipe(select(fromSettingSelectors.selectParseType))),
      switchMap(([{ payload }, parseType]) =>
        this.adminPortalApiService
          .sendRequest(
            'PATCH',
            `/v2/farms/alert/read`,
            {
              body: {
                ids: lodashMap(payload.unreadAlerts, (item) => item.alertId)
              },
              params: this.adminPortalApiService.getQueryParams({
                parseType: parseType
              })
            },
            {
              ignoreLoader: true
            }
          )
          .pipe(
            map(() => fromWalkingFarmActions.markAlertsAsReadSuccess()),
            catchError((error: HttpErrorResponse) =>
              of(
                fromWalkingFarmActions.markAlertsAsReadFailure({
                  payload: { error }
                })
              )
            )
          )
      )
    )
  );

  updatePropertiesOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromWalkingFarmActions.updatePropertiesOrder),
      filter(({ payload: { order } }) => order.length > 0),
      withLatestFrom(this.store.pipe(select(fromSettingSelectors.selectParseType))),
      switchMap(([{ payload }, parseType]) =>
        this.adminPortalApiService
          .sendRequest(
            'PATCH',
            `/v2/farms/${payload.farmId}/properties/order`,
            {
              body: payload.order,
              params: this.adminPortalApiService.getQueryParams({
                parseType: parseType
              })
            },
            {
              ignoreLoader: true
            }
          )
          .pipe(
            map(() => fromWalkingFarmActions.updatePropertiesOrderSuccess({ payload })),
            catchError((error: HttpErrorResponse) =>
              of(
                fromWalkingFarmActions.updatePropertiesOrderFailure({
                  payload: { error }
                })
              )
            )
          )
      )
    )
  );

  toggleAlertsEnabled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromWalkingFarmActions.toggleAlertsEnabled),
      withLatestFrom(this.store.pipe(select(fromSettingSelectors.selectParseType))),
      switchMap(([{ payload }, parseType]) =>
        this.adminPortalApiService
          .sendRequest('PATCH', `/v2/farms/${payload.id}`, {
            body: {
              hasFarmAlerts: payload.enabled
            },
            params: this.adminPortalApiService.getQueryParams({
              parseType: parseType
            })
          })
          .pipe(
            map(() => fromWalkingFarmActions.toggleAlertsEnabledSuccess()),
            catchError((error: HttpErrorResponse) =>
              of(
                fromWalkingFarmActions.toggleAlertsEnabledFailure({
                  payload: { error }
                })
              )
            )
          )
      )
    )
  );

  loadFarm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromWalkingFarmActions.loadFarm),
      withLatestFrom(this.store.pipe(select(fromSettingSelectors.selectParseType))),
      switchMap(
        ([
          {
            payload: { id }
          },
          parseType
        ]) =>
          this.adminPortalApiService
            .sendRequest('GET', `/v2/farms/${id}`, {
              params: this.adminPortalApiService.getQueryParams({
                parseType: parseType
              })
            })
            .pipe(
              map((response: WalkingFarm) => {
                const storedWalkingFarm: StoredWalkingFarm = {
                  boundariesData: {
                    features: [],
                    type: 'FeatureCollection'
                  },
                  boundariesEnabled: false,
                  farmId: id,
                  farmStates: [],
                  filterByLeadStatus: [],
                  filterByOwnerStatus: WalkingFarmPropertiesOwnerStatusFilterEnum.All,
                  filtersAppliedNumber: 0,
                  phoneAndEmailSelectedPropertyIds: [],
                  walkingFarm: response
                };
                const farmStates: string[] = [];
                forEach(response.properties, (property) => {
                  farmStates.push(property.A047_State);
                  if (property.ParcelWKT) {
                    const geometry: GeoJSONGeometryOrNull = parse(property.ParcelWKT);
                    if (!isNull(geometry) && geometry.type === 'Polygon') {
                      storedWalkingFarm.boundariesEnabled = true;
                      const feature: Feature = {
                        geometry: geometry,
                        properties: {
                          name: property.A001_Address
                        },
                        type: 'Feature'
                      };
                      storedWalkingFarm.boundariesData.features.push(feature);
                    }
                  }
                });
                storedWalkingFarm.farmStates = uniq(compact(farmStates));
                return fromWalkingFarmActions.setWalkingFarm({
                  payload: storedWalkingFarm
                });
              }),
              catchError((error: HttpErrorResponse) =>
                of(
                  fromWalkingFarmActions.loadFarmFailure({
                    payload: { error }
                  })
                )
              )
            )
      )
    )
  );

  deleteSavedFarm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromWalkingFarmActions.deleteSavedFarm),
      withLatestFrom(this.store.pipe(select(fromSettingSelectors.selectParseType))),
      switchMap(
        ([
          {
            payload: { id }
          },
          parseType
        ]) =>
          this.adminPortalApiService
            .sendRequest('DELETE', `/v2/farms/${id}`, {
              params: this.adminPortalApiService.getQueryParams({
                parseType: parseType
              })
            })
            .pipe(
              map(() => fromWalkingFarmActions.loadSavedFarmsList()),
              catchError((error: HttpErrorResponse) =>
                of(
                  fromWalkingFarmActions.deleteSavedFarmFailure({
                    payload: { error }
                  })
                )
              )
            )
      )
    )
  );

  loadPolygonFarmSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromWalkingFarmActions.loadPolygonFarmSummary),
      withLatestFrom(this.store.pipe(select(fromWalkingFarmSelectors.selectPolygonPoints))),
      map(([{ payload }, polygonPoints]) =>
        fromWalkingFarmActions.loadFarmSummary({
          payload: {
            searchType: payload.searchType,
            shapeJson: {
              points: lodashMap(polygonPoints, (point) => ({ x: point.lat, y: point.lng }))
            }
          }
        })
      )
    )
  );

  loadRadiusFarmSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromWalkingFarmActions.loadRadiusFarmSummary),
      withLatestFrom(
        this.store.pipe(select(fromWalkingFarmSelectors.selectDrawFarmCenter)),
        this.store.pipe(select(fromWalkingFarmSelectors.selectDrawFarmRadiusInMeters))
      ),
      filter((data) => !isNull(data[1])),
      map(([{ payload }, center, radius]) =>
        fromWalkingFarmActions.loadFarmSummary({
          payload: {
            searchType: payload.searchType,
            shapeJson: this.getPolygonPointsFromCircle(center, radius)
          }
        })
      )
    )
  );

  loadFarmSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromWalkingFarmActions.loadFarmSummary),
      withLatestFrom(
        this.store.pipe(select(fromDeviceSelectors.selectPropertyForceDeviceData)),
        this.store.pipe(select(fromWalkingFarmSelectors.selectKey)),
        this.store.pipe(select(fromWalkingFarmSelectors.selectFilters))
      ),
      map(([{ payload }, deviceData, key, filters]) => ({
        deviceData,
        filterJson: JSON.stringify({
          EndDate: filters.filterByNoSalesWithinYears ? DateTime.now().toFormat(yearMonthDayDateFormat) : null,
          MaxBathrooms: filters.filterByMaxBathrooms,
          MaxBedrooms: filters.filterByMaxBedrooms,
          MaxLivingArea: filters.filterByMaxLivingArea,
          MinBathrooms: filters.filterByMinBathrooms,
          MinBedrooms: filters.filterByMinBedrooms,
          MinLivingArea: filters.filterByMinLivingArea,
          OwnerOccupied: filters.filterByOwnerOccupied,
          SaleTypes: filters.filterBySaleTypes,
          StartDate: filters.filterByNoSalesWithinYears
            ? DateTime.now().minus({ year: filters.filterByNoSalesWithinYears }).toFormat(yearMonthDayDateFormat)
            : null,
          UseCodes: flatten(lodashMap(filters.filterByPropertyType, (item) => item.values))
        }),
        key,
        searchType: payload.searchType,
        shapeJson: JSON.stringify(payload.shapeJson)
      })),
      switchMap((query) =>
        this.propertyForceApiService
          .getData<{
            d: {
              Count: number;
              Message: string[];
              Status: string;
              StatusCode: string;
              TurnoverPercent: string;
              __type: string;
            };
          }>(
            '/GetFarmCount',
            {
              deviceData: query.deviceData,
              filterJson: query.filterJson,
              key: query.key,
              shapeJson: query.shapeJson
            },
            {
              keyRefreshType: KeyRefreshEnum.Farm,
              shouldConvertStatusCodeErrors: true,
              shouldConvertWalkingFarmCountErrors: true
            }
          )
          .pipe(
            switchMap((response) =>
              of(
                fromWalkingFarmActions.loadFarmSummarySuccess({
                  payload: {
                    messages: response.d.Message,
                    searchType: query.searchType,
                    total: response.d.Count,
                    turnoverRate: isNaN(parseFloat(response.d.TurnoverPercent))
                      ? null
                      : parseFloat(response.d.TurnoverPercent)
                  }
                })
              )
            ),
            catchError((error: HttpErrorResponse) =>
              of(
                fromWalkingFarmActions.loadFarmSummaryFailure({
                  payload: { error }
                })
              )
            )
          )
      )
    )
  );

  loadFarmDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromWalkingFarmActions.loadFarmDetails),
      withLatestFrom(
        this.store.pipe(select(fromDeviceSelectors.selectPropertyForceDeviceData)),
        this.store.pipe(select(fromWalkingFarmSelectors.selectKey)),
        this.store.pipe(select(fromWalkingFarmSelectors.selectFilters)),
        this.store.pipe(select(fromWalkingFarmSelectors.selectDrawModeType)),
        this.store.pipe(select(fromWalkingFarmSelectors.selectPolygonPoints)),
        this.store.pipe(select(fromWalkingFarmSelectors.selectDrawFarmCenter)),
        this.store.pipe(select(fromWalkingFarmSelectors.selectDrawFarmRadiusInMeters)),
        this.store.pipe(select(fromWalkingFarmSelectors.selectSummary)),
        this.store.pipe(select(fromSettingSelectors.selectApplicationKey)),
        this.store.pipe(select(fromUserSelectors.selectEmail)),
        this.store.pipe(select(fromUserSelectors.selectFarmAlertsConfiguration))
      ),
      map(
        ([
          {
            payload: { name, searchType }
          },
          deviceData,
          key,
          filters,
          drawModeType,
          points,
          radiusCenter,
          radius,
          { turnoverRate },
          appName,
          email,
          { subscribeToFarmAlerts }
        ]) => {
          const formattedFilters = {
            EndDate: filters.filterByNoSalesWithinYears ? DateTime.now().toFormat(yearMonthDayDateFormat) : null,
            MaxBathrooms: filters.filterByMaxBathrooms,
            MaxBedrooms: filters.filterByMaxBedrooms,
            MaxLivingArea: filters.filterByMaxLivingArea,
            MinBathrooms: filters.filterByMinBathrooms,
            MinBedrooms: filters.filterByMinBedrooms,
            MinLivingArea: filters.filterByMinLivingArea,
            OwnerOccupied: filters.filterByOwnerOccupied,
            SaleTypes: filters.filterBySaleTypes,
            StartDate: filters.filterByNoSalesWithinYears
              ? DateTime.now().minus({ year: filters.filterByNoSalesWithinYears }).toFormat(yearMonthDayDateFormat)
              : null,
            UseCodes: flatten(lodashMap(filters.filterByPropertyType, (item) => item.values))
          };
          let shapeJson: PolygonFarmCoordinates;
          if (drawModeType === WalkingFarmNewTabEnum.Radius) {
            shapeJson = this.getPolygonPointsFromCircle(radiusCenter, radius);
          } else {
            shapeJson = {
              points: lodashMap(points, (point) => ({ x: point.lat, y: point.lng }))
            };
          }

          return {
            adminPortalQuery: {
              appName: appName ?? '',
              email: email ?? '',
              filters: formattedFilters,
              hasFarmAlerts: subscribeToFarmAlerts,
              isPurchased: false,
              name,
              reportVersion: 1,
              shapeCoordinates: shapeJson,
              turnoverRate: `${turnoverRate}%`
            },
            propertyForceQuery: {
              deviceData,
              filterJson: JSON.stringify(formattedFilters),
              key,
              shapeJson: JSON.stringify(shapeJson)
            },
            searchType
          };
        }
      ),
      switchMap(({ adminPortalQuery, propertyForceQuery, searchType }) =>
        this.propertyForceApiService
          .getData<WalkingFarmCreateResponse>('/GetFarmReportV2', propertyForceQuery, {
            keyRefreshType: KeyRefreshEnum.Farm,
            shouldConvertStatusCodeErrors: true
          })
          .pipe(
            map((response) => response),
            withLatestFrom(this.store.pipe(select(fromSettingSelectors.selectParseType))),
            switchMap(([response, parseType]) =>
              this.adminPortalApiService
                .sendRequest('POST', '/v2/farms', {
                  body: {
                    ...adminPortalQuery,
                    properties: response.d.Properties,
                    reportCaveats: response.d.Message,
                    webServiceId: response.d.ReportId
                  },
                  params: this.adminPortalApiService.getQueryParams({
                    parseType: parseType
                  })
                })
                .pipe(
                  map((response: WalkingFarmCreateResponse) =>
                    fromWalkingFarmActions.saveFarmSuccess({ payload: { id: response.id, searchType } })
                  ),
                  catchError((error: HttpErrorResponse) =>
                    of(
                      fromWalkingFarmActions.saveFarmFailure({
                        payload: { error: new Error(error.error.description ?? error.message) }
                      })
                    )
                  )
                )
            ),
            catchError((error: HttpErrorResponse) =>
              of(
                fromWalkingFarmActions.loadFarmDetailsFailure({
                  payload: { error }
                })
              )
            )
          )
      )
    )
  );

  updateName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromWalkingFarmActions.updateName),
      withLatestFrom(this.store.pipe(select(fromSettingSelectors.selectParseType))),
      switchMap(
        ([
          {
            payload: { farmId, name }
          },
          parseType
        ]) =>
          this.adminPortalApiService
            .sendRequest('PATCH', `/v2/farms/${farmId}`, {
              body: {
                name
              },
              params: this.adminPortalApiService.getQueryParams({
                parseType: parseType
              })
            })
            .pipe(
              map(() => fromWalkingFarmActions.loadFarm({ payload: { id: farmId } })),
              catchError((error: HttpErrorResponse) => {
                let err: Error = error;
                if (error.status === 400) {
                  err = new Error(error.error.description);
                }
                return of(
                  fromWalkingFarmActions.updateNameFailure({
                    payload: { error: err }
                  })
                );
              })
            )
      )
    )
  );

  updateProperty$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromWalkingFarmActions.updateProperty),
      withLatestFrom(this.store.pipe(select(fromSettingSelectors.selectParseType))),
      switchMap(
        ([
          {
            payload: { farmId, property }
          },
          parseType
        ]) =>
          this.adminPortalApiService
            .sendRequest('PATCH', `/v2/farms/${farmId}/properties`, {
              body: {
                properties: [property]
              },
              params: this.adminPortalApiService.getQueryParams({
                parseType: parseType
              })
            })
            .pipe(
              map(() =>
                fromWalkingFarmActions.updatePropertySuccess({
                  payload: {
                    farmId,
                    property
                  }
                })
              ),
              catchError((error: HttpErrorResponse) =>
                of(
                  fromWalkingFarmActions.updatePropertyFailure({
                    payload: { error }
                  })
                )
              )
            )
      )
    )
  );

  loadFarmSummarySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromWalkingFarmActions.loadFarmSummarySuccess),
        concatMap(({ payload: { searchType } }) =>
          this.router.navigate([getMainRouteByPropertySearchType(searchType), 'save'])
        )
      ),
    {
      dispatch: false
    }
  );

  saveFarmSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromWalkingFarmActions.saveFarmSuccess),
        concatMap(({ payload: { id, searchType } }) =>
          this.router.navigate([getMainRouteByPropertySearchType(searchType), id, 'view'], {
            replaceUrl: true
          })
        )
      ),
    {
      dispatch: false
    }
  );

  exportLabels$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromWalkingFarmActions.exportLabels),
        mergeMap(({ payload }) =>
          of({ payload }).pipe(
            withLatestFrom(
              this.store.pipe(select(fromSettingSelectors.selectParseType)),
              this.store.pipe(select(fromSettingSelectors.selectReferralCode)),
              this.store.pipe(select(fromWalkingFarmSelectors.selectWalkingFarmViewFilters(payload.farmId)))
            )
          )
        ),
        filter((data) => !isNull(data[2])),
        switchMap(([{ payload }, parseType, referralCode, filters]) => {
          const body: {
            filterLeadStatus?: string[];
            filterOwnerStatus?: WalkingFarmPropertiesOwnerStatusFilterEnum;
            repCode: string;
            reportId: string;
          } = {
            filterLeadStatus: lodashMap(filters.filterByLeadStatus, camelCase),
            filterOwnerStatus: filters.filterByOwnerStatus,
            repCode: referralCode || '',
            reportId: payload.farmId
          };
          return this.reportApiService
            .sendRequest('POST', '/v3/Report/WalkingFarm/Avery5160Labels', {
              body,
              params: this.adminPortalApiService.getQueryParams({
                parseType
              }),
              responseType: 'blob'
            })
            .pipe(
              map(async (blob: Blob) => {
                await this.fileService.exportDocument(blob, payload.name, payload.name);
              }),
              catchError(async (error: HttpErrorResponse) => {
                const alert = await this.alertController.create({
                  backdropDismiss: false,
                  buttons: [
                    {
                      role: 'cancel',
                      text: 'Ok'
                    }
                  ],
                  header: 'Oops',
                  message: `We are sorry, but there was a issue exporting your labels (${error.message}).`
                });
                await alert.present();
                return of(error);
              })
            );
        })
      ),
    {
      dispatch: false
    }
  );

  exportCSV$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromWalkingFarmActions.exportCSV),
        mergeMap(({ payload }) =>
          of({ payload }).pipe(
            withLatestFrom(
              this.store.pipe(select(fromSettingSelectors.selectParseType)),
              this.store.pipe(select(fromWalkingFarmSelectors.selectWalkingFarmViewFilters(payload.farmId)))
            )
          )
        ),
        switchMap(([{ payload }, parseType, filters]) => {
          const data: {
            filterLeadStatus?: string[];
            filterOwnerStatus?: WalkingFarmPropertiesOwnerStatusFilterEnum;
            parseType: string;
          } = {
            parseType: parseType ?? ''
          };

          if (!isEmpty(filters.filterByLeadStatus)) {
            data.filterLeadStatus = lodashMap(filters.filterByLeadStatus, camelCase);
          }

          if (filters.filterByOwnerStatus) {
            data.filterOwnerStatus = filters.filterByOwnerStatus;
          }

          return this.adminPortalApiService
            .sendRequest('GET', `/v2/farms/${payload.farmId}/csv`, {
              params: this.adminPortalApiService.getQueryParams(data),
              responseType: 'blob'
            })
            .pipe(
              map(async (blob: Blob) => {
                await this.fileService.exportDocument(blob, payload.name, payload.name);
              }),
              catchError(async (error: HttpErrorResponse) => {
                const alert = await this.alertController.create({
                  backdropDismiss: false,
                  buttons: [
                    {
                      role: 'cancel',
                      text: 'Ok'
                    }
                  ],
                  header: 'Oops',
                  message: `We are sorry, but there was a issue exporting your report (${error.message}).`
                });
                await alert.present();
                return of(error);
              })
            );
        })
      ),
    {
      dispatch: false
    }
  );

  exportPDF$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromWalkingFarmActions.exportPDF),
        mergeMap(({ payload }) =>
          of({ payload }).pipe(
            withLatestFrom(
              this.store.pipe(select(fromSettingSelectors.selectParseType)),
              this.store.pipe(select(fromSettingSelectors.selectReferralCode)),
              this.store.pipe(select(fromUserSelectors.selectEmail)),
              this.store.pipe(select(fromWalkingFarmSelectors.selectWalkingFarmViewFilters(payload.farmId)))
            )
          )
        ),
        filter((data) => !isNull(data[2])),
        switchMap(([{ payload }, parseType, referralCode, email, filters]) => {
          const body: {
            email: string;
            filterLeadStatus?: string[];
            filterOwnerStatus?: WalkingFarmPropertiesOwnerStatusFilterEnum;
            propertiesPerPage: number;
            repCode: string;
            reportId: string;
          } = {
            email: email || '',
            propertiesPerPage: 4,
            repCode: referralCode || '',
            reportId: payload.farmId
          };

          if (!isEmpty(filters.filterByLeadStatus)) {
            body.filterLeadStatus = lodashMap(filters.filterByLeadStatus, camelCase);
          }

          if (filters.filterByOwnerStatus) {
            body.filterOwnerStatus = filters.filterByOwnerStatus;
          }

          return this.reportApiService
            .sendRequest('POST', '/v3/Report/Mail/WalkingFarm', {
              body,
              params: this.adminPortalApiService.getQueryParams({
                parseType
              })
            })
            .pipe(
              map(async () => {
                const alert = await this.alertController.create({
                  buttons: [
                    {
                      role: 'cancel',
                      text: 'Ok'
                    }
                  ],
                  header: 'Success',
                  message: `The PDF report was sent successfully to the following email address: ${email}.`
                });
                await alert.present();
              }),
              catchError(async (error: HttpErrorResponse) => {
                const alert = await this.alertController.create({
                  backdropDismiss: false,
                  buttons: [
                    {
                      role: 'cancel',
                      text: 'Ok'
                    }
                  ],
                  header: 'Oops',
                  message: `We are sorry, but there was a issue exporting your report (${error.message}).`
                });
                await alert.present();
                return of(error);
              })
            );
        })
      ),
    {
      dispatch: false
    }
  );

  newConfiguredFarm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromWalkingFarmActions.newConfiguredFarm),
        concatMap(async ({ payload: { searchType } }) => {
          await this.router.navigate([getMainRouteByPropertySearchType(searchType)], {
            queryParams: {
              centerOnPins: true
            }
          });
        })
      ),
    {
      dispatch: false
    }
  );

  geocodeAddressLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromWalkingFarmActions.geocodeAddressLocation),
      filter(({ payload: { address } }) => !!address),
      concatMap(({ payload: { address } }) =>
        this.mapGeocoder
          .geocode({
            address,
            componentRestrictions: {
              country: 'USA'
            }
          })
          .pipe(
            switchMap(({ results, status }) => {
              let actions: Action[];
              if (status === google.maps.GeocoderStatus.OK && !isEmpty(results)) {
                let result = results[0];
                if (results.length > 1) {
                  const streetResults = lodashFilter(results, (result) => result.types.includes('street_address'));
                  if (streetResults.length > 0) {
                    result = streetResults[0];
                    const addressResults = lodashFilter(results, (result) => isEqual(result.types, ['street_address']));
                    if (addressResults.length > 0) {
                      result = addressResults[0];
                    }
                  }
                }
                actions = [
                  fromWalkingFarmActions.geocodeLocationSuccess({
                    payload: { result }
                  }),
                  fromMapActions.updateCenter({
                    payload: {
                      location: result.geometry.location.toJSON()
                    }
                  })
                ];
              } else {
                actions = [
                  fromWalkingFarmActions.geocodeLocationFailure({
                    payload: {
                      error: new Error(
                        `No location data could be found for "${address}". Please make sure you typed a valid address.`
                      )
                    }
                  })
                ];
              }
              return actions;
            }),
            catchError((error: Error) => of(fromWalkingFarmActions.geocodeLocationFailure({ payload: { error } })))
          )
      )
    )
  );

  geocodeLocationFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromWalkingFarmActions.geocodeLocationFailure),
        concatMap(async ({ payload: { error } }) => {
          const alert = await this.alertController.create({
            backdropDismiss: false,
            buttons: [
              {
                role: 'cancel',
                text: 'Ok'
              }
            ],
            header: 'Search Error',
            message: error.message
          });
          await alert.present();
        })
      ),
    { dispatch: false }
  );

  errorAlerts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          fromWalkingFarmActions.deleteSavedFarmFailure,
          fromWalkingFarmActions.loadAlertsFailure,
          fromWalkingFarmActions.loadFarmDetailsFailure,
          fromWalkingFarmActions.loadFarmFailure,
          fromWalkingFarmActions.loadFarmSummaryFailure,
          fromWalkingFarmActions.loadPropertyAlertsFailure,
          fromWalkingFarmActions.loadSavedFarmsListFailure,
          fromWalkingFarmActions.markAlertsAsReadFailure,
          fromWalkingFarmActions.refreshKeyFailure,
          fromWalkingFarmActions.toggleAlertsEnabledFailure,
          fromWalkingFarmActions.updatePropertiesOrderFailure,
          fromWalkingFarmActions.updatePropertyFailure,
          fromWalkingFarmActions.updateNameFailure
        ),
        concatMap(async ({ payload: { error } }) => {
          const alert = await this.alertController.create({
            backdropDismiss: false,
            buttons: [
              {
                role: 'cancel',
                text: 'Ok'
              }
            ],
            header: 'Oops',
            message: error.message
          });
          await alert.present();
        })
      ),
    {
      dispatch: false
    }
  );
}
